/**
 * KYC review.
 *
 * The only tab a KYC_ADMIN can use — its endpoints are `@Roles('ADMIN',
 * 'KYC_ADMIN')` while every other admin controller is ADMIN-only. The rail
 * still shows all twelve tabs to such an account and the other eleven will
 * answer 403, which is the honest outcome: the client cannot be the thing that
 * decides, and a rail that hid tabs based on a role string would be claiming
 * an authority it does not have.
 *
 * Both actions are terminal and both send the player an email — approval and
 * rejection each have a template, and the rejection quotes the reason typed
 * here verbatim. So both go through a confirmation, and the reason is
 * collected before anything is sent.
 */
import { approveKyc, listKyc, rejectKyc } from '../admin-api';
import type { KycRow, Paged } from '../admin-api.types';
import { badge, errorText, esc, fmtDate, pager, section, table } from '../admin-kit';
import type { AdminTab } from '../admin-kit';

const STATUS_TONE: Record<string, 'ok' | 'warn' | 'bad' | 'mute'> = {
  APPROVED: 'ok',
  PENDING: 'warn',
  REJECTED: 'bad',
  NONE: 'mute',
};

export const kycTab: AdminTab = {
  id: 'kyc',
  label: 'KYC',
  group: 'Players',

  mount(host, ctx) {
    let live = true;
    let status = '';
    let pageNo = 1;
    let data: Paged<KycRow> | null = null;
    let error = '';

    function body(): string {
      if (error) return `<p class="tg-adm-empty">${esc(error)}</p>`;
      if (!data) return '<p class="tg-adm-empty">Loading…</p>';
      return `
        ${table(
    data.items,
    [
      {
        label: 'Player',
        cell: (r) => `<strong>${esc(r.username || '—')}</strong><br>
          <span class="tg-adm-mono">${esc(r.email)}</span>`,
      },
      { label: 'Submitted name', cell: (r) => esc(r.kycFullName || '—') },
      { label: 'Country', cell: (r) => esc(r.kycCountry || '—'), tight: true },
      {
        label: 'Status',
        cell: (r) => badge(r.kycStatus.toLowerCase(), STATUS_TONE[r.kycStatus] ?? 'mute'),
        tight: true,
      },
      { label: 'Submitted', cell: (r) => esc(fmtDate(r.kycSubmittedAt)), tight: true },
      { label: 'Reviewed', cell: (r) => esc(fmtDate(r.kycReviewedAt)), tight: true },
      {
        label: '',
        tight: true,
        // Only PENDING can be acted on — the endpoints refuse anything else
        // with a 400, so offering the buttons would be offering a mistake.
        cell: (r) => (r.kycStatus !== 'PENDING' ? '' : `
          <div class="tg-adm-rowacts">
            <button type="button" class="tg-adm-btn" data-act="approve" data-id="${esc(r.id)}">Approve</button>
            <button type="button" class="tg-adm-btn tg-adm-btn-danger" data-act="reject" data-id="${esc(r.id)}">Reject</button>
          </div>`),
      },
    ],
    'No submissions to review.',
  )}
        ${pager(data.page, data.pageSize, data.total)}`;
    }

    function paint(): void {
      host.innerHTML = section(
        'KYC review',
        'Identity submissions. Approving or rejecting emails the player.',
        body(),
        `<select data-status>
           <option value="">Submitted (any status)</option>
           <option value="PENDING">Pending</option>
           <option value="APPROVED">Approved</option>
           <option value="REJECTED">Rejected</option>
         </select>`,
      );
      host.querySelector<HTMLSelectElement>('[data-status]')!.value = status;
    }

    async function load(): Promise<void> {
      error = '';
      try {
        const result = await listKyc({ page: pageNo, status: status || undefined });
        if (!live) return;
        data = result;
      } catch (err) {
        if (!live) return;
        error = errorText(err);
      }
      paint();
    }

    async function act(what: string, run: () => Promise<unknown>): Promise<void> {
      try {
        await run();
        if (!live) return;
        ctx.notify(what);
      } catch (err) {
        if (!live) return;
        ctx.notify(errorText(err), 'err');
      }
      await load();
    }

    const onClick = (e: MouseEvent) => {
      const el = (e.target as HTMLElement).closest<HTMLElement>('[data-act],[data-page]');
      if (!el) return;
      if (el.dataset.page) { pageNo = Number(el.dataset.page); void load(); return; }
      const id = el.dataset.id!;
      if (el.dataset.act === 'approve') {
        ctx.confirm({
          title: 'Approve this submission?',
          body: 'The player is marked verified and receives the approval email.',
          confirmLabel: 'Approve',
          onConfirm: () => void act('Submission approved.', () => approveKyc(id)),
        });
      } else if (el.dataset.act === 'reject') {
        ctx.prompt({
          title: 'Reject this submission?',
          body: 'The reason below is emailed to the player word for word.',
          label: 'Reason',
          confirmLabel: 'Reject',
          onConfirm: (reason) => void act('Submission rejected.', () => rejectKyc(id, reason)),
        });
      }
    };

    const onChange = (e: Event) => {
      const el = e.target as HTMLSelectElement;
      if (!el.hasAttribute('data-status')) return;
      status = el.value;
      pageNo = 1;
      void load();
    };

    host.addEventListener('click', onClick);
    host.addEventListener('change', onChange);

    paint();
    void load();

    return () => {
      live = false;
      host.removeEventListener('click', onClick);
      host.removeEventListener('change', onChange);
    };
  },
};
